import { useState } from "react";
import { Phone, Mail, MapPin, Sparkles, Building2, Sun, Layers, Wrench, Droplets, CheckCircle2, Star, ArrowRight, Menu, X, Shield, Clock, Award, HelpCircle } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Toaster, toast } from "sonner";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_RAW = "3035981953";
const PHONE_DISPLAY = "(303) 598-1953";
const EMAIL = "altitudecleanwindows@gmail.com";

const LOGO = "https://customer-assets.emergentagent.com/job_altitude-co/artifacts/y90efqmn_ACW_Logo_JPG.jpg";

const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_altitude-co/artifacts/th0mibqi_Landing%20Page.png",
  detail: "https://static.prod-images.emergentagent.com/jobs/38cd218d-ceae-477a-ba7b-fc4d80131bb0/images/22964ecb997c12882985840bd71f18ca76a327e297617f2e8a3f08ddfdb1ab3d.png",
  warm: "https://static.prod-images.emergentagent.com/jobs/38cd218d-ceae-477a-ba7b-fc4d80131bb0/images/1a6e525ebc70e0873bb793446c5f053af619b02c59ad8b692845f20b0a372b94.png",
  commercial: "https://customer-assets.emergentagent.com/job_altitude-co/artifacts/8vf07i74_Commercial.png",
  residential: "https://customer-assets.emergentagent.com/job_altitude-co/artifacts/ttadwamh_Residential.png",
  solar: "https://images.unsplash.com/photo-1719848576338-9516ba7ccd8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVsJTIwY2xlYW5pbmclMjBzdW5ueXxlbnwwfHx8fDE3NzkxNDUwMTN8MA&ixlib=rb-4.1.0&q=85",
  denver: "https://images.unsplash.com/photo-1648441095877-90406e6ba04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkZW52ZXIlMjBjb2xvcmFkbyUyMHNreWxpbmUlMjBkYXl8ZW58MHx8fHwxNzc5MTQ1MDEzfDA&ixlib=rb-4.1.0&q=85",
  gallery1: "https://images.squarespace-cdn.com/content/v1/650c5ea19cd6d00f5e10def4/9d978605-4026-4002-82fc-f9b04da50ae7/IMG_1733.jpeg",
  gallery2: "https://images.squarespace-cdn.com/content/v1/650c5ea19cd6d00f5e10def4/46ea3df2-9c9f-4306-9112-380ad4229bea/IMG_1736.jpeg",
  gallery3: "https://images.squarespace-cdn.com/content/v1/650c5ea19cd6d00f5e10def4/bf94331c-bd74-45f7-abac-faa775f42e62/IMG_1746.jpeg",
};

const SERVICES = [
  { id: "residential", title: "Residential Window Cleaning", icon: Sparkles, blurb: "Interior + exterior windows, screens, tracks, sills, mirrors, and shower glass. Treated like family — your home, your view.", img: IMG.residential, span: "lg:col-span-7" },
  { id: "commercial", title: "Commercial Window Cleaning", icon: Building2, blurb: "Storefronts, offices, and multi-story buildings across Metro Denver. Flexible scheduling, reliable crews, spotless results.", img: IMG.commercial, span: "lg:col-span-5" },
  { id: "solar", title: "Solar Panel Cleaning", icon: Sun, blurb: "Boost panel efficiency up to 30%. Dust, pollen, and Colorado grit gently removed — every kilowatt counts.", img: IMG.solar, span: "lg:col-span-5" },
  { id: "screen-clean", title: "Screen Cleaning", icon: Layers, blurb: "Removed, hand-cleaned, and reinstalled. Lets in more light and the Colorado breeze you actually want.", span: "lg:col-span-4" },
  { id: "screen-repair", title: "Screen Repair", icon: Wrench, blurb: "Torn or sagging screens re-meshed and rebuilt. Cheaper than full replacement and good as new.", span: "lg:col-span-3" },
  { id: "gutter", title: "Gutter Cleaning", icon: Droplets, blurb: "Clear downspouts, debris removed, water flowing where it should. Protect your roof and foundation.", span: "lg:col-span-12" },
];

const TESTIMONIALS = [
  { name: "Kim Nelms", text: "ACW really shines! Put us on your schedule." },
  { name: "Annie Wyse", text: "I didn't know my windows could look this good." },
  { name: "Mitchell Vyles", text: "I can see! I didn't realize how dirty our windows were. Thanks Mike." },
  { name: "Otto Zerr", text: "I can see out my windows now..." },
];

const AREAS = ["Denver", "Aurora", "Lakewood", "Centennial", "Highlands Ranch", "Littleton", "Englewood", "Wheat Ridge", "Arvada", "Westminster", "Thornton", "Golden", "Broomfield", "Greenwood Village"];

const FAQS = [
  {
    q: "How much does window cleaning cost in Denver?",
    a: "Pricing depends on home size, number of windows, story height, and whether screens or tracks need work. Most single-family homes in Metro Denver fall between $150 and $400. Call (303) 598-1953 or request a free quote — we'll give you an honest, flat number before we start."
  },
  {
    q: "How often should I have my windows cleaned in Colorado?",
    a: "Most Front Range homeowners book us twice a year — once in spring after pollen and snowmelt grime, and again in early fall before the holidays. Commercial storefronts often clean monthly or quarterly. Hail, dust storms, and construction nearby may bump that up."
  },
  {
    q: "Which Denver-area neighborhoods do you serve?",
    a: "We serve Denver, Aurora, Lakewood, Centennial, Highlands Ranch, Littleton, Englewood, Wheat Ridge, Arvada, Westminster, Thornton, Golden, Broomfield, Greenwood Village, and the surrounding Metro Denver communities."
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes — Altitude Clean Windows, LLC is fully licensed and insured in Colorado. Every crew member is covered, so your home, family, and property are protected on every job."
  },
  {
    q: "Do you clean solar panels and gutters too?",
    a: "Absolutely. Clean solar panels can produce up to 30% more energy. We also clear gutters and downspouts to protect your roof and foundation through Denver's wet seasons. Most homeowners bundle these with their window cleaning to save a trip."
  },
  {
    q: "Can you clean windows before a real estate listing or after construction?",
    a: "Yes — real estate prep and post-construction cleanup are two of our specialties. We remove paint, stickers, and construction debris from glass, frames, tracks, sills, and screens, so the property photographs and shows beautifully."
  },
  {
    q: "Do you offer streak-free guarantees?",
    a: "Yes. If you spot a streak when we finish, point it out and we'll redo it on the spot — no charge, no attitude. It's our standard."
  },
];



function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#services", label: "Services" },
    { href: "#work", label: "Our Work" },
    { href: "#areas", label: "Service Area" },
    { href: "#about", label: "About" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAFAF9]/80 border-b border-[#E7E5E4]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src={LOGO} alt="Altitude Clean Windows logo" className="h-11 w-11 md:h-12 md:w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-bold tracking-tight text-[#1C1917]">Altitude Clean Windows</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#57534E]">Metro Denver, CO</div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-sm font-medium text-[#1C1917] hover:text-[#0284C7] transition-colors" data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>{l.label}</a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${PHONE_RAW}`} className="text-sm font-semibold text-[#1C1917] hover:text-[#0284C7] flex items-center gap-2" data-testid="nav-phone">
            <Phone className="h-4 w-4 text-[#E07A5F]" /> {PHONE_DISPLAY}
          </a>
          <Button asChild className="bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-full px-5" data-testid="nav-cta">
            <a href="#contact">Get a Quote</a>
          </Button>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#E7E5E4] bg-[#FAFAF9]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map(l => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-2 text-base font-medium text-[#1C1917]" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}>{l.label}</a>
            ))}
            <a href={`tel:${PHONE_RAW}`} className="py-2 text-base font-semibold text-[#0284C7] flex items-center gap-2"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
            <Button asChild className="bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-full mt-2" data-testid="mobile-cta">
              <a href="#contact" onClick={() => setOpen(false)}>Get a Quote</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8F3] via-[#FAFAF9] to-[#E0F2FE]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 animate-fade-up">
          <div className="flex items-center gap-4 mb-6">
            <img src={LOGO} alt="Altitude Clean Windows logo" className="h-16 w-16 md:h-20 md:w-20 object-contain" data-testid="hero-logo" />
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-[#E7E5E4] text-xs font-semibold uppercase tracking-[0.14em] text-[#57534E]" data-testid="hero-badge">
              <span className="h-2 w-2 rounded-full bg-[#E07A5F]" /> Locally owned • Metro Denver
            </div>
          </div>
          <h1 className="font-heading font-bold tracking-tight text-[#1C1917] text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-balance" data-testid="hero-headline">
            See Colorado <span className="italic font-quote text-[#0284C7]">clearly.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg md:text-xl text-[#57534E] leading-relaxed" data-testid="hero-subhead">
            Streak-free residential & commercial window cleaning across Denver and Metro Denver. Screens, solar panels, and gutters too — done right by people who actually care.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-full px-7 h-12 text-base shadow-lg shadow-[#0284C7]/20" data-testid="hero-cta-quote">
              <a href="#contact" className="inline-flex items-center gap-2">Get a Free Quote <ArrowRight className="h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7 h-12 text-base border-[#1C1917]/15 hover:bg-white" data-testid="hero-cta-call">
              <a href={`tel:${PHONE_RAW}`} className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
            </Button>
          </div>
          <div className="mt-10 flex items-center gap-6">
            <div className="flex -space-x-1.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-[#E07A5F] text-[#E07A5F]" />)}
            </div>
            <p className="text-sm text-[#57534E]">Loved by Denver homeowners — <span className="font-semibold text-[#1C1917]">"ACW really shines."</span></p>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#0284C7]/10 border border-white">
            <img src={IMG.hero} alt="Modern home with sparkling clean windows in Denver, Colorado" className="w-full h-[420px] md:h-[560px] object-cover" data-testid="hero-image" />
          </div>
          <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-xl rounded-2xl border border-[#E7E5E4] px-5 py-4 shadow-xl items-center gap-4" data-testid="hero-trust-badge">
            <div className="h-10 w-10 rounded-full bg-[#E0F2FE] flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#0284C7]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">Licensed & Insured</div>
              <div className="font-heading font-semibold text-[#1C1917]">Mike & Crew</div>
            </div>
          </div>
          <div className="hidden md:flex absolute -top-5 -right-5 bg-[#E07A5F] text-white rounded-2xl px-5 py-3 shadow-xl flex-col" data-testid="hero-rating-badge">
            <div className="text-2xl font-heading font-bold leading-none">5.0★</div>
            <div className="text-[11px] uppercase tracking-[0.14em] opacity-90 mt-1">Client Rated</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { icon: Award, label: "Locally Owned", value: "Family Run" },
    { icon: Shield, label: "Licensed", value: "& Insured" },
    { icon: Clock, label: "Response", value: "Same Day" },
    { icon: CheckCircle2, label: "Guaranteed", value: "Streak-Free" },
  ];
  return (
    <section className="border-y border-[#E7E5E4] bg-white" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <div key={i} className="flex items-center gap-4" data-testid={`stat-${i}`}>
            <div className="h-12 w-12 rounded-2xl bg-[#FFF8F3] flex items-center justify-center">
              <s.icon className="h-5 w-5 text-[#E07A5F]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">{s.label}</div>
              <div className="font-heading font-bold text-[#1C1917]">{s.value}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="py-24 md:py-32 px-6 md:px-10" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">Our Services</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-tight">
            Everything that lets the light in — <span className="font-quote italic text-[#0284C7]">spotless.</span>
          </h2>
          <p className="mt-5 text-lg text-[#57534E] max-w-2xl">From single-family homes in Highlands Ranch to storefronts in downtown Denver — six core services, one careful crew.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {SERVICES.map(s => (
            <article key={s.id} className={`group relative overflow-hidden rounded-3xl border border-[#E7E5E4] bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${s.span}`} data-testid={`service-card-${s.id}`}>
              {s.img && (
                <div className="relative h-48 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
              )}
              <div className="p-7 md:p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-[#E0F2FE] flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-[#0284C7]" />
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-[#1C1917]">{s.title}</h3>
                </div>
                <p className="text-[#57534E] text-base leading-relaxed">{s.blurb}</p>
                <a href="#contact" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0284C7] group-hover:gap-3 transition-all" data-testid={`service-cta-${s.id}`}>
                  Request this service <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const items = [
    { img: IMG.gallery1, label: "Residential — Lakewood" },
    { img: IMG.gallery2, label: "Two-story brick — Arvada" },
    { img: IMG.gallery3, label: "Second-story detail — Denver" },
    { img: IMG.detail, label: "Sparkling close-up" },
  ];
  return (
    <section id="work" className="py-24 md:py-32 px-6 md:px-10 bg-[#FFF8F3]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">Completed Works</div>
            <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl">Recent jobs across the Front Range.</h2>
          </div>
          <a href="#contact" className="text-sm font-semibold text-[#0284C7] inline-flex items-center gap-2" data-testid="gallery-cta">Book yours <ArrowRight className="h-4 w-4" /></a>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((it, i) => (
            <figure key={i} className="group relative rounded-2xl overflow-hidden aspect-[3/4] bg-white" data-testid={`gallery-item-${i}`}>
              <img src={it.img} alt={it.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white text-sm font-medium">{it.label}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 opacity-10">
        <img src={IMG.warm} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">What Our Clients Say</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl">Real Denver homeowners. <span className="font-quote italic text-[#0284C7]">Real reactions.</span></h2>
        </div>
      </div>
      <Marquee gradient gradientColor="#FAFAF9" gradientWidth={80} speed={36} pauseOnHover>
        {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
          <figure key={i} className="mx-4 my-2 w-[340px] md:w-[420px] bg-white rounded-3xl border border-[#E7E5E4] p-7 md:p-8 shadow-sm" data-testid={`testimonial-${i}`}>
            <div className="flex gap-0.5 mb-4">
              {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#E07A5F] text-[#E07A5F]" />)}
            </div>
            <blockquote className="font-quote text-2xl md:text-3xl leading-snug text-[#1C1917]">&ldquo;{t.text}&rdquo;</blockquote>
            <figcaption className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-[#57534E]">— {t.name}</figcaption>
          </figure>
        ))}
      </Marquee>
    </section>
  );
}

function ServiceArea() {
  return (
    <section id="areas" className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden" data-testid="service-area-section">
      <div className="absolute inset-0">
        <img src={IMG.denver} alt="Denver, Colorado skyline" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C4A6E]/95 via-[#0C4A6E]/85 to-[#0C4A6E]/70" />
      </div>
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FCD34D] mb-3">Service Area</div>
          <h2 className="font-heading font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-tight">Proudly serving the entire Metro Denver area.</h2>
          <p className="mt-5 text-lg text-sky-100 max-w-xl">If you can see the Front Range from your window, we can clean it. Locally based, locally trusted.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-white text-[#0C4A6E] hover:bg-sky-50 rounded-full px-7" data-testid="areas-cta-quote">
              <a href="#contact">Get a Free Quote</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7 border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="areas-cta-call">
              <a href={`tel:${PHONE_RAW}`}><Phone className="h-4 w-4 mr-2" /> {PHONE_DISPLAY}</a>
            </Button>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="areas-list">
            {AREAS.map(a => (
              <div key={a} className="px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 text-white text-sm font-medium flex items-center gap-2" data-testid={`area-${a.toLowerCase().replace(/\s/g, '-')}`}>
                <MapPin className="h-3.5 w-3.5 text-[#FCD34D]" /> {a}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-10" data-testid="about-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden border border-[#E7E5E4]">
            <img src={IMG.gallery2} alt="Mike from Altitude Clean Windows on the job in Denver" className="w-full h-[440px] object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">About Altitude Clean Windows</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl leading-tight">A locally owned crew that <span className="font-quote italic text-[#0284C7]">treats every home like our own.</span></h2>
          <p className="mt-6 text-lg text-[#57534E] leading-relaxed">
            Altitude Clean Windows, LLC is a family-run window cleaning company based right here in Metro Denver. Founded by Mike, ACW was built on a simple idea: show up on time, work carefully, and leave windows so clean clients say <em className="font-quote text-[#1C1917]">"I can see!"</em>
          </p>
          <p className="mt-4 text-lg text-[#57534E] leading-relaxed">
            We&apos;re fully insured, courteous on every property, and obsessive about the small things — clean tracks, dry sills, screens reinstalled exactly where they came from.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {["Interior + exterior cleaning", "Track, sill & screen care", "Real estate listing prep", "Post-construction cleanup", "Solar panel servicing", "Gutter cleaning"].map(item => (
              <li key={item} className="flex items-center gap-2.5 text-[#1C1917]" data-testid={`about-feature-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CheckCircle2 className="h-5 w-5 text-[#0284C7]" /> {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function QuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", service_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in name, email, phone, and service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Thanks! We received your request.", { description: `We'll reply within one business day, or call ${PHONE_DISPLAY} for same-day service.` });
      setForm({ name: "", email: "", phone: "", address: "", service_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E7E5E4] p-7 md:p-9 shadow-sm" data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-1">
          <Label htmlFor="qf-name" className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Full Name *</Label>
          <Input id="qf-name" value={form.name} onChange={e => update("name", e.target.value)} placeholder="Jane Smith" className="mt-2 h-12 rounded-xl border-[#E7E5E4]" data-testid="quote-input-name" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="qf-email" className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Email *</Label>
          <Input id="qf-email" type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="jane@example.com" className="mt-2 h-12 rounded-xl border-[#E7E5E4]" data-testid="quote-input-email" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="qf-phone" className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Phone *</Label>
          <Input id="qf-phone" type="tel" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(303) 555-0123" className="mt-2 h-12 rounded-xl border-[#E7E5E4]" data-testid="quote-input-phone" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="qf-address" className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Address / Neighborhood</Label>
          <Input id="qf-address" value={form.address} onChange={e => update("address", e.target.value)} placeholder="Denver, CO" className="mt-2 h-12 rounded-xl border-[#E7E5E4]" data-testid="quote-input-address" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Service Needed *</Label>
          <Select value={form.service_type} onValueChange={v => update("service_type", v)}>
            <SelectTrigger className="mt-2 h-12 rounded-xl border-[#E7E5E4]" data-testid="quote-select-service">
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map(s => <SelectItem key={s.id} value={s.title} data-testid={`quote-option-${s.id}`}>{s.title}</SelectItem>)}
              <SelectItem value="Multiple Services" data-testid="quote-option-multiple">Multiple / Not Sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="qf-msg" className="text-xs uppercase tracking-[0.14em] text-[#57534E] font-semibold">Tell us about the job</Label>
          <Textarea id="qf-msg" value={form.message} onChange={e => update("message", e.target.value)} rows={4} placeholder="How many windows? Stories? Any specific concerns?" className="mt-2 rounded-xl border-[#E7E5E4]" data-testid="quote-input-message" />
        </div>
      </div>
      <Button type="submit" disabled={submitting} className="mt-7 w-full sm:w-auto bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-full h-12 px-8 text-base" data-testid="quote-submit">
        {submitting ? "Sending…" : (<span className="inline-flex items-center gap-2">Request Free Quote <ArrowRight className="h-4 w-4" /></span>)}
      </Button>
      <p className="mt-4 text-xs text-[#57534E]">We&apos;ll reply within one business day. Or call <a href={`tel:${PHONE_RAW}`} className="text-[#0284C7] font-semibold">{PHONE_DISPLAY}</a> for immediate scheduling.</p>
    </form>
  );
}

function FAQ() {
  return (
    <section id="faq" className="py-24 md:py-32 px-6 md:px-10 bg-white" data-testid="faq-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">Frequently Asked</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl leading-tight">Window cleaning <span className="font-quote italic text-[#0284C7]">questions,</span> answered for Denver.</h2>
          <p className="mt-5 text-lg text-[#57534E]">Honest answers on pricing, frequency, service area, and what to expect when we show up. Don't see your question? <a href={`tel:${PHONE_RAW}`} className="text-[#0284C7] font-semibold">Give us a call</a>.</p>
          <div className="mt-8 hidden lg:block">
            <div className="rounded-3xl bg-[#FFF8F3] border border-[#E7E5E4] p-7">
              <HelpCircle className="h-6 w-6 text-[#E07A5F] mb-3" />
              <p className="font-quote text-2xl text-[#1C1917] leading-snug">Still wondering? <br />Just call <a href={`tel:${PHONE_RAW}`} className="text-[#0284C7]">{PHONE_DISPLAY}</a> — Mike usually picks up.</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-[#FAFAF9] rounded-2xl border border-[#E7E5E4] px-6" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-heading font-semibold text-lg text-[#1C1917] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#57534E] text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  const reasons = [
    { title: "Local Denver Crew", desc: "Family-run and headquartered in Metro Denver. We know the altitude, the dust, the hail, and the pollen — and how to handle it." },
    { title: "Insured & Reliable", desc: "Fully licensed and insured in Colorado. We show up when we say we will, and we treat your property like our own." },
    { title: "Streak-Free Promise", desc: "If there's a streak when we finish, we redo it on the spot. No questions, no extra charge." },
    { title: "All Exterior Services", desc: "Windows, screens, solar panels, and gutters in one visit. One trusted crew, one tidy invoice." },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-10 bg-[#FAFAF9]" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">Why Denver Homeowners Choose Us</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl leading-tight">A locally trusted name for Metro Denver window cleaning.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reasons.map((r, i) => (
            <div key={i} className="p-7 rounded-3xl bg-white border border-[#E7E5E4] hover:border-[#0284C7] transition-colors" data-testid={`why-card-${i}`}>
              <div className="h-10 w-10 rounded-xl bg-[#E0F2FE] flex items-center justify-center mb-4">
                <CheckCircle2 className="h-5 w-5 text-[#0284C7]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#1C1917]">{r.title}</h3>
              <p className="mt-2 text-[#57534E] leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-10 bg-[#FFF8F3]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E07A5F] mb-3">Get In Touch</div>
          <h2 className="font-heading font-bold tracking-tight text-[#1C1917] text-4xl md:text-5xl leading-tight">Let&apos;s get those windows <span className="font-quote italic text-[#0284C7]">sparkling.</span></h2>
          <p className="mt-5 text-lg text-[#57534E]">Tell us a little about your place — we&apos;ll send a friendly, no-pressure quote.</p>

          <div className="mt-9 space-y-4">
            <a href={`tel:${PHONE_RAW}`} className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E7E5E4] hover:border-[#0284C7] transition-colors group" data-testid="contact-phone-card">
              <div className="h-12 w-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center group-hover:bg-[#0284C7] transition-colors">
                <Phone className="h-5 w-5 text-[#0284C7] group-hover:text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">Call or Text</div>
                <div className="font-heading font-bold text-lg text-[#1C1917]">{PHONE_DISPLAY}</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E7E5E4] hover:border-[#0284C7] transition-colors group" data-testid="contact-email-card">
              <div className="h-12 w-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center group-hover:bg-[#0284C7] transition-colors">
                <Mail className="h-5 w-5 text-[#0284C7] group-hover:text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">Email</div>
                <div className="font-heading font-bold text-lg text-[#1C1917] break-all">{EMAIL}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E7E5E4]" data-testid="contact-area-card">
              <div className="h-12 w-12 rounded-xl bg-[#FFF8F3] flex items-center justify-center">
                <MapPin className="h-5 w-5 text-[#E07A5F]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">Serving</div>
                <div className="font-heading font-bold text-lg text-[#1C1917]">Metro Denver, Colorado</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-[#E7E5E4]" data-testid="contact-hours-card">
              <div className="h-12 w-12 rounded-xl bg-[#FFF8F3] flex items-center justify-center">
                <Clock className="h-5 w-5 text-[#E07A5F]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#57534E]">Hours</div>
                <div className="font-heading font-bold text-lg text-[#1C1917]">Mon–Sat, 7am–6pm</div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <QuoteForm />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1C1917] text-stone-300 px-6 md:px-10 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#0284C7] to-[#0EA5E9] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="font-heading font-bold text-white text-lg" data-testid="footer-business-name">Altitude Clean Windows</div>
          </div>
          <div className="text-sm font-semibold text-sky-400 mb-3" data-testid="footer-service-area">Serving the Denver Metro Area</div>
          <p className="text-sm leading-relaxed max-w-md">Professional window cleaning, screen care, solar panel cleaning, and gutter cleaning across Denver and Metro Denver, Colorado. Locally owned and fully insured.</p>
          <div className="mt-5 space-y-2 text-sm">
            <a href={`tel:${PHONE_RAW}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-white break-all" data-testid="footer-email"><Mail className="h-4 w-4" /> {EMAIL}</a>
          </div>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-4">Services</div>
          <ul className="space-y-2.5 text-sm">
            {SERVICES.map(s => <li key={s.id}><a href="#services" className="hover:text-white">{s.title}</a></li>)}
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-4">Service Area</div>
          <p className="text-sm leading-relaxed">{AREAS.join(" • ")} and surrounding Metro Denver communities.</p>
          <Button asChild className="mt-5 bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-full" data-testid="footer-cta">
            <a href="#contact">Get a Free Quote</a>
          </Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-14 pt-6 border-t border-stone-800 flex flex-col md:flex-row gap-3 justify-between text-xs text-stone-500">
        <div>© {new Date().getFullYear()} Altitude Clean Windows, LLC. All rights reserved.</div>
        <a href="#contact" className="hover:text-white underline underline-offset-4" data-testid="footer-contact-link">Contact Us</a>
        <div>Window Cleaning Denver • Window Cleaning Aurora • Window Cleaning Lakewood • Solar Panel Cleaning Colorado</div>
      </div>
    </footer>
  );
}

// =================== Main App ===================

function App() {
  return (
    <div className="App font-body bg-[#FAFAF9] text-[#1C1917]" data-testid="app-root">
      <Toaster richColors position="top-center" />
      <Nav />
      <main>
        <Hero />
        <Stats />
        <Services />
        <Gallery />
        <Testimonials />
        <ServiceArea />
        <About />
        <WhyChoose />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
